import {Component, computed, viewChild, effect, ChangeDetectionStrategy} from "@angular/core"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent} from "@template-editor/components/value-slot/value-slot.component"
import {
    TemplateGraph,
    PointGeometryJoinOperator,
    CurveGeometryJoinOperator,
    MeshGeometryJoinOperator,
    PointGeometryGetAttributeSet,
    CurveGeometryGetAttributeSet,
    MeshGeometryGetAttributeSet,
    MeshGeometryGetAttribute,
    MeshGeometryTriangulateOperator,
    GeometryJoinOperatorNode,
    GeometryProductOperatorNode,
    GeometryProjectOperatorNode,
    GeometryFilterOperatorNode,
    GeometryPartitionOperatorNode,
    GeometryFlipOperatorNode,
    GeometryBoundaryOperatorNode,
    GeometryMergeOperatorNode,
    GeometrySplitOperatorNode,
    GeometryGetAttributeSetNode,
    GeometryAttributeSetNode,
    IndexingDim,
    isGeometryAttributeSet,
    getGeometryAttributeSetLike,
    getGeometryAttributeLike,
    JOIN_OPERATOR_OUTPUT_A,
    JOIN_OPERATOR_OUTPUT_B,
    PRODUCT_OPERATOR_OUTPUT_A,
    PRODUCT_OPERATOR_OUTPUT_B,
    PointGeometryProductOperator,
    CurveGeometryProductOperator,
    MeshGeometryProductOperator,
    MeshGeometryCutOperator,
    CUT_OPERATOR_OUTPUT_CUT_MESH_ATTRIBUTE_SET,
    CUT_OPERATOR_OUTPUT_WINDING_ATTRIBUTE_SET,
    PARTITION_OPERATOR_OUTPUT_A_ATTRIBUTE_SET,
    PARTITION_OPERATOR_OUTPUT_B_ATTRIBUTE_SET,
    PointGeometryPartitionOperator,
    CurveGeometryPartitionOperator,
    MeshGeometryPartitionOperator,
    MeshGeometrySubdivideOperator,
    SUBDIVIDE_OPERATOR_OUTPUT_POSITION_ATTR,
    SUBDIVIDE_OPERATOR_OUTPUT_NORMAL_ATTR,
    SUBDIVIDE_OPERATOR_OUTPUT_PRIMITIVE_UNIFORM_ATTRS,
    SUBDIVIDE_OPERATOR_OUTPUT_VERTEX_VARYING_ATTRS,
} from "@cm/template-nodes"
import {GeometryAttributeSetInspectorComponent} from "@template-editor/components/inspectors/geometry-graph-node-inspector/components/geometry-attribute-set-inspector/geometry-attribute-set-inspector.component"
import {createSelectorDummyNode} from "@template-editor/components/inspectors/geometry-graph-node-inspector/components/helpers"
import {z} from "zod/v4-mini"

class GetGeometryAttributeLikeContainerDummyNode extends createSelectorDummyNode(
    z.object({output: getGeometryAttributeLike}),
    "GetGeometryAttributeLikeContainerDummyNode",
) {}

class GetGeometryAttributeSetLikeContainerDummyNode extends createSelectorDummyNode(
    z.object({output: getGeometryAttributeSetLike}),
    "GetGeometryAttributeSetLikeContainerDummyNode",
) {}

// TODO make detection of outputs automatic

function getJoinOperatorLikeOutput(node: GeometryJoinOperatorNode, output: typeof JOIN_OPERATOR_OUTPUT_A | typeof JOIN_OPERATOR_OUTPUT_B) {
    const outputSetForKey = (() => {
        if (node instanceof PointGeometryJoinOperator)
            return new PointGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else if (node instanceof CurveGeometryJoinOperator)
            return new CurveGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else if (node instanceof MeshGeometryJoinOperator)
            return new MeshGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else throw new Error(`Unsupported join operator type`)
    })()

    return new GetGeometryAttributeSetLikeContainerDummyNode({parameters: {output: outputSetForKey}})
}

function getProductOperatorLikeOutput(node: GeometryProductOperatorNode, output: typeof PRODUCT_OPERATOR_OUTPUT_A | typeof PRODUCT_OPERATOR_OUTPUT_B) {
    const outputSetForKey = (() => {
        if (node instanceof PointGeometryProductOperator)
            return new PointGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else if (node instanceof CurveGeometryProductOperator)
            return new CurveGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else if (node instanceof MeshGeometryProductOperator)
            return new MeshGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else throw new Error(`Unsupported product operator type`)
    })()

    return new GetGeometryAttributeSetLikeContainerDummyNode({parameters: {output: outputSetForKey}})
}

// The ProductOperator carries its input pair as a concrete sub-class instance (not a string option).
// The dropdown lists `operator.availableInputClasses` (owned by the node); the value is each input
// class's `nodeClass` string, and on change we hand it back to `operator.setOperatorInput(...)`.
const productOperatorInputLabels: Record<string, string> = {
    PointGeometryProductOperatorPointGeometryPointGeometryInput: "Point  +  Point",
    CurveGeometryProductOperatorPointGeometryCurveGeometryInput: "Point  +  Curve",
    MeshGeometryProductOperatorPointGeometryMeshGeometryInput: "Point  +  Mesh",
    MeshGeometryProductOperatorCurveGeometryCurveGeometryInput: "Curve  +  Curve",
    MeshGeometryProductOperatorCurveGeometryMeshGeometryInput: "Curve  +  Mesh",
}

class ProductOperatorInputSelectionDummyNode extends createSelectorDummyNode<{inputClass: string}>(
    z.object({inputClass: z.string()}),
    "ProductOperatorInputSelectionDummyNode",
) {}

function getCutOperatorCutMeshAttributeSetOutput(node: MeshGeometryCutOperator) {
    return new GetGeometryAttributeSetLikeContainerDummyNode({
        parameters: {
            output: new MeshGeometryGetAttributeSet({
                parameters: {
                    name: CUT_OPERATOR_OUTPUT_CUT_MESH_ATTRIBUTE_SET,
                    attributeSetName: CUT_OPERATOR_OUTPUT_CUT_MESH_ATTRIBUTE_SET,
                    attributeSet: node,
                },
            }),
        },
    })
}

function getCutOperatorWindingAttributeSetOutput(node: MeshGeometryCutOperator) {
    return new GetGeometryAttributeSetLikeContainerDummyNode({
        parameters: {
            output: new MeshGeometryGetAttributeSet({
                parameters: {
                    name: CUT_OPERATOR_OUTPUT_WINDING_ATTRIBUTE_SET,
                    attributeSetName: CUT_OPERATOR_OUTPUT_WINDING_ATTRIBUTE_SET,
                    attributeSet: node,
                },
            }),
        },
    })
}

function getPartitionOperatorLikeOutput(
    node: GeometryPartitionOperatorNode,
    output: typeof PARTITION_OPERATOR_OUTPUT_A_ATTRIBUTE_SET | typeof PARTITION_OPERATOR_OUTPUT_B_ATTRIBUTE_SET,
) {
    const outputSetForKey = (() => {
        if (node instanceof PointGeometryPartitionOperator)
            return new PointGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else if (node instanceof CurveGeometryPartitionOperator)
            return new CurveGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else if (node instanceof MeshGeometryPartitionOperator)
            return new MeshGeometryGetAttributeSet({
                parameters: {
                    name: output,
                    attributeSetName: output,
                    attributeSet: node,
                },
            })
        else throw new Error(`Unsupported partition operator type`)
    })()

    return new GetGeometryAttributeSetLikeContainerDummyNode({parameters: {output: outputSetForKey}})
}

function getSubdivideOperatorPositionAttributeOutput(node: MeshGeometrySubdivideOperator) {
    return new GetGeometryAttributeLikeContainerDummyNode({
        parameters: {
            output: new MeshGeometryGetAttribute({
                parameters: {
                    name: SUBDIVIDE_OPERATOR_OUTPUT_POSITION_ATTR,
                    attributeName: SUBDIVIDE_OPERATOR_OUTPUT_POSITION_ATTR,
                    attributeSet: node,
                },
            }),
        },
    })
}
function getSubdivideOperatorNormalAttributeOutput(node: MeshGeometrySubdivideOperator) {
    return new GetGeometryAttributeLikeContainerDummyNode({
        parameters: {
            output: new MeshGeometryGetAttribute({
                parameters: {
                    name: SUBDIVIDE_OPERATOR_OUTPUT_NORMAL_ATTR,
                    attributeName: SUBDIVIDE_OPERATOR_OUTPUT_NORMAL_ATTR,
                    attributeSet: node,
                },
            }),
        },
    })
}
function getSubdivideOperatorVertexVaryingAttributesOutput(node: MeshGeometrySubdivideOperator) {
    return new GetGeometryAttributeSetLikeContainerDummyNode({
        parameters: {
            output: new MeshGeometryGetAttributeSet({
                parameters: {
                    name: SUBDIVIDE_OPERATOR_OUTPUT_VERTEX_VARYING_ATTRS,
                    attributeSetName: SUBDIVIDE_OPERATOR_OUTPUT_VERTEX_VARYING_ATTRS,
                    attributeSet: node,
                },
            }),
        },
    })
}
function getSubdivideOperatorPrimitiveUniformAttributesOutput(node: MeshGeometrySubdivideOperator) {
    return new GetGeometryAttributeSetLikeContainerDummyNode({
        parameters: {
            output: new MeshGeometryGetAttributeSet({
                parameters: {
                    name: SUBDIVIDE_OPERATOR_OUTPUT_PRIMITIVE_UNIFORM_ATTRS,
                    attributeSetName: SUBDIVIDE_OPERATOR_OUTPUT_PRIMITIVE_UNIFORM_ATTRS,
                    attributeSet: node,
                },
            }),
        },
    })
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-geometry-attribute-set-like-node-inspector",
    templateUrl: "./geometry-attribute-set-like-node-inspector.component.html",
    styleUrls: ["./geometry-attribute-set-like-node-inspector.component.scss"],
    standalone: true,
    imports: [InspectorSectionComponent, ValueSlotComponent, GeometryAttributeSetInspectorComponent],
})
export class GeometryAttributeSetLikeNodeInspectorComponent extends BaseInspectorComponent<GeometryAttributeSetNode<IndexingDim>> {
    readonly $productOperatorOperatorParameterSelection = viewChild<ValueSlotComponent<ProductOperatorInputSelectionDummyNode>>(
        "productOperatorOperatorParameterSelection",
    )

    readonly $attributeSetNode = computed(() => {
        const node = this.$node()
        return node ? (isGeometryAttributeSet(node) ? node : null) : null
    })

    readonly $getAttributeSetLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryGetAttributeSetNode ? node : null
    })

    readonly $joinOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryJoinOperatorNode ? node : null
    })
    readonly $joinOperatorLikeOutputSetA = computed(() => {
        const node = this.$joinOperatorLike()
        if (!node) return null
        return getJoinOperatorLikeOutput(node, JOIN_OPERATOR_OUTPUT_A)
    })
    readonly $joinOperatorLikeOutputSetB = computed(() => {
        const node = this.$joinOperatorLike()
        if (!node) return null
        return getJoinOperatorLikeOutput(node, JOIN_OPERATOR_OUTPUT_B)
    })

    readonly $triangulateOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof MeshGeometryTriangulateOperator ? node : null
    })

    readonly $productOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryProductOperatorNode ? node : null
    })
    readonly $productOperatorLikeOperatorParameter = computed(() => {
        this.$parameters()
        const node = this.$productOperatorLike()
        if (!node) return null
        return node.parameters.operator
    })
    readonly $productOperatorLikeOutputSetA = computed(() => {
        const node = this.$productOperatorLike()
        if (!node) return null
        return getProductOperatorLikeOutput(node, PRODUCT_OPERATOR_OUTPUT_A)
    })
    readonly $productOperatorLikeOutputSetB = computed(() => {
        const node = this.$productOperatorLike()
        if (!node) return null
        return getProductOperatorLikeOutput(node, PRODUCT_OPERATOR_OUTPUT_B)
    })
    readonly $projectOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryProjectOperatorNode ? node : null
    })

    readonly $cutOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof MeshGeometryCutOperator ? node : null
    })
    readonly $cutOperatorLikeCutMeshAttributeSetOutput = computed(() => {
        const node = this.$cutOperatorLike()
        if (!node) return null
        return getCutOperatorCutMeshAttributeSetOutput(node)
    })
    readonly $cutOperatorLikeWindingAttributeSetOutput = computed(() => {
        const node = this.$cutOperatorLike()
        if (!node) return null
        return getCutOperatorWindingAttributeSetOutput(node)
    })

    readonly $filterOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryFilterOperatorNode ? node : null
    })

    readonly $partitionOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryPartitionOperatorNode ? node : null
    })
    readonly $partitionOperatorLikeOutputSetA = computed(() => {
        const node = this.$partitionOperatorLike()
        if (!node) return null
        return getPartitionOperatorLikeOutput(node, PARTITION_OPERATOR_OUTPUT_A_ATTRIBUTE_SET)
    })
    readonly $partitionOperatorLikeOutputSetB = computed(() => {
        const node = this.$partitionOperatorLike()
        if (!node) return null
        return getPartitionOperatorLikeOutput(node, PARTITION_OPERATOR_OUTPUT_B_ATTRIBUTE_SET)
    })

    readonly $flipOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryFlipOperatorNode ? node : null
    })

    readonly $boundaryOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryBoundaryOperatorNode ? node : null
    })

    readonly $mergeOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometryMergeOperatorNode ? node : null
    })

    readonly $splitOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof GeometrySplitOperatorNode ? node : null
    })

    readonly $subdivideOperatorLike = computed(() => {
        const node = this.$node()
        return node instanceof MeshGeometrySubdivideOperator ? node : null
    })

    readonly $subdivideOperatorLikePositionAttributeOutput = computed(() => {
        const node = this.$subdivideOperatorLike()
        if (!node) return null
        return getSubdivideOperatorPositionAttributeOutput(node)
    })
    readonly $subdivideOperatorLikeNormalAttributeOutput = computed(() => {
        const node = this.$subdivideOperatorLike()
        if (!node) return null
        return getSubdivideOperatorNormalAttributeOutput(node)
    })
    readonly $subdivideOperatorLikeVertexVaryingAttributesOutput = computed(() => {
        const node = this.$subdivideOperatorLike()
        if (!node) return null
        return getSubdivideOperatorVertexVaryingAttributesOutput(node)
    })
    readonly $subdivideOperatorLikePrimitiveUniformAttributesOutput = computed(() => {
        const node = this.$subdivideOperatorLike()
        if (!node) return null
        return getSubdivideOperatorPrimitiveUniformAttributesOutput(node)
    })

    // Seeded empty; the constructor effect syncs it to the selected operator's actual input class.
    private readonly productOperatorInputSelectionDummyNode = new ProductOperatorInputSelectionDummyNode({
        parameters: {inputClass: ""},
    })

    readonly $productOperatorOperatorParameterSelectionPossibilities = computed(() => {
        const productOperator = this.$productOperatorLike()
        if (!productOperator) return null
        return {
            node: this.productOperatorInputSelectionDummyNode,
            possibilities: productOperator.availableInputClasses.map((Ctor) => ({
                value: Ctor.getNodeClass(),
                name: productOperatorInputLabels[Ctor.getNodeClass()] ?? Ctor.getNodeClass(),
            })),
        }
    })

    constructor() {
        super()

        effect(() => {
            const productOperator = this.$productOperatorLike()
            this.$productOperatorLikeOperatorParameter()
            if (!productOperator) return
            this.productOperatorInputSelectionDummyNode.updateParametersExplicit({inputClass: productOperator.parameters.operator.nodeClass})
            this.$productOperatorOperatorParameterSelection()?.forceTriggerRecompute()
        })
    }

    onProductOperatorOperatorParameterSelectionChange(newInputClass: unknown) {
        const productOperator = this.$productOperatorLike()
        if (!productOperator || typeof newInputClass !== "string") return
        this.modifyTemplateGraph(() => productOperator.setOperatorInput(newInputClass))
    }

    modifyTemplateGraph = (modifier: (x: TemplateGraph) => void) => this.sceneManagerService.modifyTemplateGraph(modifier)
}
